import config from "@Config/index";
import {
  getAvailableStartingPricing,
  getAvailableStartingPricingHelper,
  getPricingCurrency,
} from "../SearchListItem/helper";

export const CLUSTER_SOURCE_ID = "randomPoints";
export const CLUSTER_MARKER_LAYER_ID = "cluster-marker";
export const SEARCH_MAP_CLUSTER_MAX_ZOOM = 14;
export const MAP_STYLE_STANDARD = "mapbox://styles/mapbox/standard";
export const MAP_STYLE_STREETS = "mapbox://styles/mapbox/streets-v12";
export const BUILDINGS_3D_LAYER_ID = "amber-search-3d-buildings";
export const HOVER_HIGHLIGHT_SOURCE_ID = "hover-highlight-source";
export const HOVER_HIGHLIGHT_LAYER_ID = "hover-highlight-point";

const HIGHLIGHT_TRANSITION_MS = 250;

const HIGHLIGHT_OPACITY_TRANSITION = {
  duration: HIGHLIGHT_TRANSITION_MS,
  delay: 0,
};

const UNCLUSTERED_FILTER = ["!", ["has", "point_count"]] as const;

// Use strict equality — Mapbox's boolean expression treats non-empty strings as true,
// so a string "false" would incorrectly hide the default layer on initial load.
const IS_HIGHLIGHTED = ["==", ["get", "highlighted"], true] as const;

const defaultLayerOpacityPaint = {
  "icon-opacity": ["case", IS_HIGHLIGHTED, 0, 1],
  "text-opacity": ["case", IS_HIGHLIGHTED, 0, 1],
  "icon-opacity-transition": HIGHLIGHT_OPACITY_TRANSITION,
  "text-opacity-transition": HIGHLIGHT_OPACITY_TRANSITION,
};

const highlightedLayerOpacityPaint = {
  "icon-opacity": ["case", IS_HIGHLIGHTED, 1, 0],
  "text-opacity": ["case", IS_HIGHLIGHTED, 1, 0],
  "icon-opacity-transition": HIGHLIGHT_OPACITY_TRANSITION,
  "text-opacity-transition": HIGHLIGHT_OPACITY_TRANSITION,
};

let hoverHighlightFadeTimeout: ReturnType<typeof setTimeout> | null = null;
let clusterImageFallbackCleanup: (() => void) | null = null;

export const CLUSTER_LAYER_IDS = [
  CLUSTER_MARKER_LAYER_ID,
  "unclustered-point",
  "unclustered-highlighted-point",
  HOVER_HIGHLIGHT_LAYER_ID,
] as const;

export const getInventoryPriceValue = (pricing: any) => {
  const price = getAvailableStartingPricingHelper(pricing);
  if (typeof price === "number") return price;
  const parsed = parseFloat(String(price ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getClusterCurrencySymbol = (data: any[]) => {
  const first = data.find((it) => it?.pricing);
  return getPricingCurrency(first?.pricing?.currency) ?? "£";
};

export const UNCLUSTERED_LAYER_IDS = [
  "unclustered-point",
  "unclustered-highlighted-point",
] as const;

export const MAP_VIEW_LIST_SCROLL_EVENT = "MAP_VIEW_LIST_SCROLL";

export const DEFAULT_MAP_PADDING = 32;

export type MapViewState = {
  center: [number, number];
  zoom: number;
};

export const getQueryableLayers = (map: any, layerIds: readonly string[]) =>
  layerIds.filter((layerId) => Boolean(map?.getLayer(layerId)));

export const queryRenderedFeaturesSafe = (
  map: any,
  options: Record<string, unknown>,
  layerIds: readonly string[],
) => {
  const layers = getQueryableLayers(map, layerIds);
  if (!layers.length) return [];

  try {
    return map.queryRenderedFeatures({ ...options, layers });
  } catch {
    return [];
  }
};

export const isInventoryVisibleOnMap = (
  map: any,
  coordinates: number[],
  padding = DEFAULT_MAP_PADDING,
) => {
  if (!map?.getBounds || !coordinates?.length) return false;

  const bounds = map.getBounds();
  if (!bounds.contains(coordinates)) return false;

  const point = map.project(coordinates);
  const { width, height } = map.getCanvas();
  return (
    point.x >= padding &&
    point.x <= width - padding &&
    point.y >= padding &&
    point.y <= height - padding
  );
};

export const panMapToCoordinates = (map: any, coordinates: number[]) => {
  if (!map || !coordinates?.length) return;

  map.easeTo({
    center: coordinates,
    zoom: map.getZoom(),
    duration: 400,
    essential: true,
    padding: DEFAULT_MAP_PADDING,
    ...getMapCameraPreserveOptions(map),
  });
};

export const getMapCameraPreserveOptions = (map: any) => ({
  pitch: map?.getPitch?.() ?? 0,
  bearing: map?.getBearing?.() ?? 0,
});

export const enableStreets3DBuildingsLayer = (map: any) => {
  if (!map?.isStyleLoaded?.() || map.getLayer(BUILDINGS_3D_LAYER_ID)) return;
  if (!map.getSource("composite")) return;

  const labelLayer = map
    .getStyle()
    ?.layers?.find((layer: any) => layer.type === "symbol" && layer.layout?.["text-field"]);

  try {
    map.addLayer(
      {
        id: BUILDINGS_3D_LAYER_ID,
        source: "composite",
        "source-layer": "building",
        filter: ["==", ["get", "extrude"], "true"],
        type: "fill-extrusion",
        minzoom: 15,
        paint: {
          "fill-extrusion-color": "#d9d9d9",
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.65,
        },
      },
      labelLayer?.id,
    );
  } catch {
    // Style may already include its own building extrusion layer.
  }
};

export const configureMap3DFeatures = (map: any, enabled: boolean) => {
  if (!map) return;

  const apply = () => {
    try {
      if (typeof map.setConfigProperty === "function") {
        map.setConfigProperty("basemap", "show3DObjects", enabled);
        map.setConfigProperty("basemap", "show3DLandmarks", enabled);
      }
    } catch {
      // Standard style config is unavailable for this style.
    }

    if (enabled) {
      enableStreets3DBuildingsLayer(map);
      map.getStyle()?.layers?.forEach((layer: any) => {
        if (layer.type === "fill-extrusion" && layer["source-layer"] === "building") {
          map.setLayoutProperty(layer.id, "visibility", "visible");
        }
      });
      return;
    }

    if (map.getLayer(BUILDINGS_3D_LAYER_ID)) {
      map.setLayoutProperty(BUILDINGS_3D_LAYER_ID, "visibility", "none");
    }
  };

  if (map.isStyleLoaded()) {
    apply();
  } else {
    map.once("style.load", apply);
  }
};

export const easeMapToViewState = (map: any, viewState: MapViewState) => {
  if (!map || !viewState) return;

  map.easeTo({
    center: viewState.center,
    zoom: viewState.zoom,
    duration: 500,
    ...getMapCameraPreserveOptions(map),
    essential: true,
  });
};

export const getInventoryPriceLabel = (pricing: any) =>
  (getAvailableStartingPricing(pricing) || "H")?.replace("₹", "INR ");

export const generateClusterGeoJSON = (data: any[]) => ({
  type: "FeatureCollection",
  features: data.map((it: any) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [
        it?.location?.location_coordinates?.lng,
        it?.location?.location_coordinates?.lat,
      ],
    },
    properties: {
      id: it.id,
      price: getInventoryPriceLabel(it?.pricing),
      priceValue: getInventoryPriceValue(it?.pricing),
      icon: "defaultPricePin",
      text: "#111928",
      highlighted: false,
    },
  })),
});

export const addClusterImages = (map: any) =>
  Promise.all(
    [
      {
        url: `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/price-tag-default.png`,
        id: "defaultPricePin",
      },
      {
        url: `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/price-tag-fill.png`,
        id: "focustPricePin",
      },
    ].map(
      (imageData) =>
        new Promise((resolve, reject) => {
          map.loadImage(imageData.url, (error: any, image: any) => {
            if (error) {
              reject(error);
              return;
            }
            if (!map.hasImage(imageData.id)) {
              map.addImage(imageData.id, image);
            }
            resolve(image);
          });
        }),
    ),
  );

export const removeClusterLayersAndSource = (map: any) => {
  if (!map) return;
  if (hoverHighlightFadeTimeout) {
    clearTimeout(hoverHighlightFadeTimeout);
    hoverHighlightFadeTimeout = null;
  }
  if (clusterImageFallbackCleanup) {
    clusterImageFallbackCleanup();
    clusterImageFallbackCleanup = null;
  }
  const layersToRemove = [...CLUSTER_LAYER_IDS, "clusters", "cluster-count"];
  layersToRemove.forEach((id) => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  if (map.getSource(HOVER_HIGHLIGHT_SOURCE_ID)) map.removeSource(HOVER_HIGHLIGHT_SOURCE_ID);
  if (map.getSource(CLUSTER_SOURCE_ID)) map.removeSource(CLUSTER_SOURCE_ID);
};

export const setClusterHoverHighlight = (
  map: any,
  feature: { coordinates: number[]; price: string; id: string } | null,
) => {
  const source = map?.getSource(HOVER_HIGHLIGHT_SOURCE_ID);
  if (!source || !map?.getLayer(HOVER_HIGHLIGHT_LAYER_ID)) return;

  if (hoverHighlightFadeTimeout) {
    clearTimeout(hoverHighlightFadeTimeout);
    hoverHighlightFadeTimeout = null;
  }

  if (!feature) {
    map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "icon-opacity", 0);
    map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "text-opacity", 0);
    hoverHighlightFadeTimeout = setTimeout(() => {
      if (!map.getSource(HOVER_HIGHLIGHT_SOURCE_ID)) return;
      source.setData({ type: "FeatureCollection", features: [] });
      map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "icon-opacity", 1);
      map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "text-opacity", 1);
      hoverHighlightFadeTimeout = null;
    }, HIGHLIGHT_TRANSITION_MS);
    return;
  }

  source.setData({
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "Point", coordinates: feature.coordinates },
        properties: {
          id: feature.id,
          price: feature.price,
          icon: "focustPricePin",
          text: "#fff",
        },
      },
    ],
  });

  map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "icon-opacity", 0);
  map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "text-opacity", 0);
  requestAnimationFrame(() => {
    if (!map.getLayer(HOVER_HIGHLIGHT_LAYER_ID)) return;
    map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "icon-opacity", 1);
    map.setPaintProperty(HOVER_HIGHLIGHT_LAYER_ID, "text-opacity", 1);
  });
};

export const findClusterForInventory = (
  map: any,
  id: string,
  coordinates: number[],
): Promise<{ coordinates: number[] } | null> =>
  new Promise((resolve) => {
    const visibleFeatures = queryRenderedFeaturesSafe(
      map,
      { filter: ["==", ["get", "id"], id] },
      UNCLUSTERED_LAYER_IDS,
    );
    if (visibleFeatures.length) {
      resolve(null);
      return;
    }

    const clusterLayers = getQueryableLayers(map, [CLUSTER_MARKER_LAYER_ID]);
    if (!clusterLayers.length) {
      resolve(null);
      return;
    }

    const point = map.project(coordinates);
    let clusterFeatures: any[] = [];
    try {
      clusterFeatures = map.queryRenderedFeatures(
        [
          [point.x - 30, point.y - 30],
          [point.x + 30, point.y + 30],
        ],
        { layers: clusterLayers },
      );
    } catch {
      resolve(null);
      return;
    }

    if (!clusterFeatures.length) {
      resolve(null);
      return;
    }

    const source = map.getSource(CLUSTER_SOURCE_ID);
    let pending = clusterFeatures.length;
    let found: { coordinates: number[] } | null = null;

    clusterFeatures.forEach((clusterFeature: any) => {
      const clusterId = clusterFeature.properties.cluster_id;
      source.getClusterLeaves(clusterId, Infinity, 0, (err: any, leaves: any[]) => {
        pending -= 1;
        if (!found && !err && leaves.some((leaf) => leaf.properties.id === id)) {
          found = { coordinates: clusterFeature.geometry.coordinates };
        }
        if (pending === 0) {
          resolve(found);
        }
      });
    });
  });

export const addClusterSourceAndLayers = (map: any, geojsonData: any) => {
  removeClusterLayersAndSource(map);

  map.addSource(CLUSTER_SOURCE_ID, {
    type: "geojson",
    data: geojsonData,
    cluster: true,
    clusterMaxZoom: SEARCH_MAP_CLUSTER_MAX_ZOOM,
    clusterRadius: 50,
    clusterProperties: {
      minPrice: ["min", ["get", "priceValue"]],
    },
  });

  map.addLayer({
    id: CLUSTER_MARKER_LAYER_ID,
    type: "circle",
    source: CLUSTER_SOURCE_ID,
    filter: ["has", "point_count"],
    paint: {
      "circle-color": "#ed3a56",
      "circle-radius": [
        "step",
        ["get", "point_count"],
        20,
        10, // Radius size for small clusters
        30,
        50, // Radius size for medium clusters
        40, // Radius size for large clusters
      ],
      "circle-stroke-width": 1,
      "circle-stroke-color": "#FFFFFF",
    },
  });

  map.addLayer({
    id: "cluster-count",
    type: "symbol",
    source: CLUSTER_SOURCE_ID,
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["Arial Unicode MS Bold"],
      "text-size": 16,
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": "#FFFFFF",
    },
  });

  map.addLayer({
    id: "unclustered-point",
    type: "symbol",
    source: CLUSTER_SOURCE_ID,
    filter: UNCLUSTERED_FILTER,
    layout: {
      "text-field": [
        "format",
        ["get", "price"],
        {
          "font-scale": 1,
          "text-color": "#111928",
        },
      ],
      "text-size": 16,
      "text-font": ["Arial Unicode MS Bold"],
      "icon-text-fit-padding": [6, 18, 22, 18],
      "icon-image": "defaultPricePin",
      "icon-text-fit": "both",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "symbol-sort-key": ["case", IS_HIGHLIGHTED, 1, 0],
    },
    paint: defaultLayerOpacityPaint,
  });

  map.addLayer({
    id: "unclustered-highlighted-point",
    type: "symbol",
    source: CLUSTER_SOURCE_ID,
    filter: UNCLUSTERED_FILTER,
    layout: {
      "text-field": [
        "format",
        ["get", "price"],
        {
          "font-scale": 1,
          "text-color": "#fff",
        },
      ],
      "text-size": 16,
      "text-font": ["Arial Unicode MS Bold"],
      "icon-text-fit-padding": [6, 18, 22, 18],
      "icon-image": "focustPricePin",
      "icon-size": 1.05,
      "icon-text-fit": "both",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "symbol-sort-key": ["case", IS_HIGHLIGHTED, 2, 0],
    },
    paint: highlightedLayerOpacityPaint,
  });

  map.addSource(HOVER_HIGHLIGHT_SOURCE_ID, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  map.addLayer({
    id: HOVER_HIGHLIGHT_LAYER_ID,
    type: "symbol",
    source: HOVER_HIGHLIGHT_SOURCE_ID,
    layout: {
      "text-field": [
        "format",
        ["get", "price"],
        {
          "font-scale": 1,
          "text-color": ["get", "text"],
        },
      ],
      "text-size": 16,
      "text-font": ["Arial Unicode MS Bold"],
      "icon-text-fit-padding": [6, 18, 22, 18],
      "icon-image": ["get", "icon"],
      "icon-size": 1.05,
      "icon-text-fit": "both",
      "icon-allow-overlap": true,
    },
    paint: {
      "icon-opacity": 1,
      "text-opacity": 1,
      "icon-opacity-transition": HIGHLIGHT_OPACITY_TRANSITION,
      "text-opacity-transition": HIGHLIGHT_OPACITY_TRANSITION,
    },
  });
};

export const fitMapToClusterBounds = (
  map: any,
  geojsonData: any,
  mapboxgl: any,
  onComplete?: (viewState: MapViewState) => void,
) => {
  const bounds = new mapboxgl.LngLatBounds();
  geojsonData.features.forEach((feature: any) => {
    bounds.extend(feature.geometry.coordinates);
  });
  map.fitBounds(bounds, {
    padding: DEFAULT_MAP_PADDING,
    maxZoom: SEARCH_MAP_CLUSTER_MAX_ZOOM,
    duration: 1000,
    ...getMapCameraPreserveOptions(map),
  });

  if (onComplete) {
    map.once("moveend", () => {
      onComplete({
        center: map.getCenter().toArray(),
        zoom: map.getZoom(),
      });
    });
  }
};
